/**
 * Weight Freeze (v2) — Requests domain logic.
 *
 * Plain module, no React import. Every derivation the Requests screens need
 * — round grouping, the submission budget, tone/label lookups, search and
 * level filtering, the one mutation — lives here so it is cheap to unit
 * test and impossible to accidentally couple to a component.
 *
 * ROUND BOUNDARIES COME FROM `event.kind`, NEVER FROM PROSE. The offline
 * mockup this module replaces (`RqRounds`/`RqEventMeta`) ran regexes over
 * English sentences ("additional data requested", "approv…") to decide
 * where one review round ended and the next began, and string-stripped an
 * "Additional data requested:" prefix to recover what Prozo had asked for.
 * Our dataset stores typed events — `{date, actor, round, kind, text,
 * requestedItems?}` with `kind` drawn from `EVENT_KIND` — so every one of
 * those regexes becomes a lookup keyed on `kind`, and `requestedItems`
 * (already an array on the event) replaces the stripped-prefix string
 * entirely. That survives a copy edit, i18n, and a backend that generates
 * history instead of a fixture.
 *
 * VOCABULARIES ARE STRING ENUMS. `REVIEW_STATE`, `REJECT_REASON`,
 * `STATUS_TONE` (data/reference.js) are objects keyed on a stable string
 * value, not positions in an array. Every lookup below (`STATE_META`,
 * `REASON_GUIDE`) is keyed the same way, so reordering the enum's own
 * declaration can never relabel a request — the old mockup's
 * `RqStateTone`/`RqReasonGuide` array-index ternaries are exactly the bug
 * this guards against.
 */
import {REVIEW_STATE, REJECT_REASON, EVENT_KIND, STATUS_TONE, RULES} from '../data/reference'
import {todayISO as clockToday} from '../data/clock'

export const MAX_SUBMISSIONS = RULES.SUBMISSIONS_PER_REQUEST
export const PRELIM_CYCLES = RULES.PRELIMINARY_CYCLES

/**
 * The module clock (data/clock.js): the dataset's pinned NOW when seeded, the wall clock on a
 * live mount. Never Date.now(), never bare new Date() — go through the clock.
 */
export function todayISO() {
  return clockToday()
}

/** A Prozo auto-proposal waiting on the merchant (T12/T13). Seeded data never carries one. */
export function isProposal(req) {
  return !!req && req.kind === 'review' && req.state === REVIEW_STATE.PROPOSED
}

/**
 * Who a merchant-side event is attributed to. In a real panel this is the
 * authenticated user; here it follows the merchant's freeze type, which is
 * the same rule the seeded history was written with.
 */
export function actorFor(merchant) {
  return merchant && merchant.type && merchant.type.freeze === 'prozo' ? 'Prozo Ops' : (merchant && merchant.name) || 'You'
}

/** Approval is preliminary strictly under PRELIM_CYCLES elapsed billing cycles. */
export function isPreliminary(req) {
  return ((req && req.cyclesElapsed) || 0) < PRELIM_CYCLES
}

export function submissionsUsed(req) {
  return Math.min(MAX_SUBMISSIONS, Math.max(0, (req && req.submissions) || 0))
}

export function submissionsLeft(req) {
  return MAX_SUBMISSIONS - submissionsUsed(req)
}

/**
 * A merchant may only send additional data once PROZO HAS ASKED FOR IT.
 * REVIEW_STATE.ADDITIONAL is the single state that carries an ask (AWAITING
 * has nothing outstanding, SUBMITTED is already back with Prozo), and the
 * budget still has to have room — both conditions, never either.
 */
export function canSubmit(req) {
  return !!req
    && req.kind === 'review'
    && req.state === REVIEW_STATE.ADDITIONAL
    && submissionsUsed(req) < MAX_SUBMISSIONS
}

export function packOf(packs, packId) {
  return (packs || []).find((p) => p.id === packId) || null
}

/**
 * The five stat tiles. Deliberately computed over the WHOLE collection and
 * never over the filtered one — the tiles answer "where does this merchant
 * stand", the tab counts answer "where did my search land". Keeping them as
 * two functions (this one and filterRequests below) is what stops someone
 * "fixing" the tiles to follow the filters.
 */
export function requestCounts(requests) {
  const all = requests || []
  return {
    approved: all.filter((r) => r.kind === 'approved').length,
    prelim: all.filter((r) => r.kind === 'approved' && isPreliminary(r)).length,
    review: all.filter((r) => r.kind === 'review').length,
    asked: all.filter((r) => r.kind === 'review' && r.state === REVIEW_STATE.ADDITIONAL).length,
    // Prozo proposals waiting on the merchant — a subset of `review`, like `asked`.
    proposed: all.filter((r) => r.kind === 'review' && r.state === REVIEW_STATE.PROPOSED).length,
    rejected: all.filter((r) => r.kind === 'rejected').length,
  }
}

/** `level` compares against the STORED value ('Combo'), never the label ('Combination'). */
export function filterRequests(requests, {q = '', level = ''} = {}, packs = []) {
  const s = String(q || '').trim().toLowerCase()
  const keep = (r) => {
    if (level && r.level !== level) return false
    if (!s) return true
    const pack = packOf(packs, r.packId)
    return [r.id, r.subject, r.level, r.reason, r.approvedBy, r.raisedBy, r.state, pack && pack.label]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().indexOf(s) >= 0)
  }
  const kept = (requests || []).filter(keep)
  return {
    approved: kept.filter((r) => r.kind === 'approved'),
    review: kept.filter((r) => r.kind === 'review'),
    rejected: kept.filter((r) => r.kind === 'rejected'),
  }
}

/**
 * Outcome of one round, from the KINDS recorded in it. The mockup ran four
 * regexes over English prose to get here; our events are typed, so this is a
 * set lookup and survives a copy edit, i18n and backend-generated history.
 */
export function roundOutcome(events) {
  const list = events || []
  if (!list.length) return {outcome: 'Open, nothing recorded yet', tone: 'neutral'}
  const kinds = new Set(list.map((e) => e.kind))
  if (kinds.has(EVENT_KIND.APPROVED)) return {outcome: 'Approved on this round', tone: 'ok'}
  if (kinds.has(EVENT_KIND.DECLINED)) return {outcome: 'Declined on this round', tone: 'danger'}
  const asked = kinds.has(EVENT_KIND.INFO_REQUESTED)
  const sent = kinds.has(EVENT_KIND.SUBMITTED)
  if (asked && sent) return {outcome: 'Not approved, more data asked for and supplied', tone: 'info'}
  if (asked) return {outcome: 'Not approved, more data asked for and still open', tone: 'info'}
  if (sent) return {outcome: 'Data supplied, waiting on Prozo', tone: 'info'}
  return {outcome: 'Under Prozo review', tone: 'warn'}
}

/**
 * Round groups, 1..N contiguous. N is the higher of the last round anything
 * was recorded in and the request's current round, so (a) an open round with
 * nothing in it is still shown and (b) a gap in the middle — WF-NB-2609
 * records on rounds 1 and 3 — renders as an explicit empty round rather than
 * silently collapsing 3 into 2.
 */
export function groupRounds(history, currentRound) {
  const events = history || []
  const byRound = new Map()
  events.forEach((e) => {
    const n = e.round || 1
    if (!byRound.has(n)) byRound.set(n, [])
    byRound.get(n).push(e)
  })
  const highest = events.reduce((max, e) => Math.max(max, e.round || 1), 0)
  const top = Math.max(highest, currentRound || 0)
  const groups = []
  for (let n = 1; n <= top; n += 1) {
    const list = byRound.get(n) || []
    groups.push({round: n, events: list, ...roundOutcome(list)})
  }
  return groups
}

/** The most recent Prozo ask, for the review card's "Prozo has asked for more data" banner. */
export function latestInfoRequest(history) {
  const events = history || []
  for (let i = events.length - 1; i >= 0; i -= 1) {
    if (events[i].kind === EVENT_KIND.INFO_REQUESTED) return events[i]
  }
  return null
}

/** Timeline dot tone per event kind. The icon component lives in RqTimeline.js. */
export const EVENT_TONE = {
  [EVENT_KIND.RAISED]: 'neutral',
  [EVENT_KIND.SUBMITTED]: 'info',
  [EVENT_KIND.INFO_REQUESTED]: 'info',
  [EVENT_KIND.NOTE]: 'neutral',
  [EVENT_KIND.APPROVED]: 'ok',
  [EVENT_KIND.DECLINED]: 'danger',
  [EVENT_KIND.LIMIT_REACHED]: 'warn',
  [EVENT_KIND.WITHDRAWN]: 'neutral',
  [EVENT_KIND.SUPERSEDED]: 'neutral',
  [EVENT_KIND.PROPOSED]: 'ai',
  [EVENT_KIND.PROPOSAL_ACCEPTED]: 'ok',
  [EVENT_KIND.PROPOSAL_DECLINED]: 'danger',
}

/**
 * Keyed on the vocabulary STRING, not on its position in an array — a
 * reorder of REVIEW_STATE used to silently mislabel every request.
 * `key` drives the icon choice in the review card; `tone` drives the chip.
 */
const STATE_META = {
  [REVIEW_STATE.AWAITING]: {tone: STATUS_TONE.review, key: 'awaiting'},
  [REVIEW_STATE.ADDITIONAL]: {tone: STATUS_TONE.additional, key: 'additional'},
  [REVIEW_STATE.SUBMITTED]: {tone: STATUS_TONE.submitted, key: 'submitted'},
  [REVIEW_STATE.PROPOSED]: {tone: STATUS_TONE.preliminary, key: 'proposed'},
}

export function stateMeta(state) {
  return STATE_META[state] || {tone: STATUS_TONE.none, key: 'unknown'}
}

/** Verbatim guidance per reject reason, keyed on the REJECT_REASON enum value. */
const REASON_GUIDE = {
  [REJECT_REASON.CONTRADICTORY]:
    'Prozo held shipment data that disagreed with the weights in this request. A fresh request '
    + 'needs evidence that reconciles with the shipments Prozo can already see, not more of the same.',
  [REJECT_REASON.COURIER_SIDE]:
    'The courier cannot hold a frozen weight for this shipment profile, so the outcome does not '
    + 'depend on more evidence. A fresh request only helps once the packing material or the courier '
    + 'mix behind these shipments changes.',
  [REJECT_REASON.INADEQUATE]:
    'All ' + MAX_SUBMISSIONS + ' submissions, being the 1 initial one plus 2 additional rounds, were '
    + 'used and the evidence still did not support the weight. A fresh request starts the '
    + MAX_SUBMISSIONS + '-submission budget again from zero.',
}

export function reasonGuide(reason) {
  return REASON_GUIDE[reason] || 'Prozo declined this request.'
}

/**
 * The module's ONLY mutation. Pure: returns a new request object with a new
 * history array and never touches the one it was given, so the frozen
 * dataset export stays frozen and React sees a genuinely new reference.
 *
 * NOT PERSISTED. A future RequestsSection.js holds the result in the shared
 * store; there is no API call and a page reload discards it.
 */
export function applySubmission(req, payload, {actor, today}) {
  // Returning the request untouched is the established no-op contract here —
  // RequestsSection bails on `preview === req` — so refusing costs no new
  // signalling path.
  if (!canSubmit(req)) return req
  const used = submissionsUsed(req)
  const nextUsed = used + 1
  const nextRound = Math.min(MAX_SUBMISSIONS, (req.round || 1) + 1)
  const ships = Number(payload.ships) || 0
  const note = String(payload.note || '').trim()

  const events = [{
    date: today,
    actor,
    round: nextRound,
    kind: EVENT_KIND.SUBMITTED,
    text: 'Additional data submitted: ' + payload.kindLabel
      + (ships ? ', covering ' + ships.toLocaleString('en-IN') + ' shipments' : '')
      + ' (submission ' + nextUsed + ' of ' + MAX_SUBMISSIONS + ')',
  }]
  if (note) {
    events.push({date: today, actor, round: nextRound, kind: EVENT_KIND.NOTE, text: 'Note to Prozo: ' + note})
  }
  if (nextUsed >= MAX_SUBMISSIONS) {
    events.push({
      date: today, actor: 'System', round: nextRound, kind: EVENT_KIND.LIMIT_REACHED,
      text: 'Submission limit reached. Prozo will decide on the evidence on file.',
    })
  }

  return {
    ...req,
    submissions: nextUsed,
    round: nextRound,
    state: REVIEW_STATE.SUBMITTED,
    history: (req.history || []).concat(events),
  }
}
